import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';

export type NextStepType =
  | 'follow_up_2d'
  | 'follow_up_7d'
  | 'schedule_meeting'
  | 'move_stage'
  | 'none';

interface NextStepModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadName: string;
  outcome: string;
  onConfirm: (nextStep: {
    type: NextStepType;
    date?: Date;
    stage?: string;
  }) => void;
}

const nextStepPresets: Array<{
  value: NextStepType;
  label: string;
  description: string;
  days?: number;
}> = [
  { value: 'follow_up_2d', label: 'Follow up in 2 days', description: 'Remind me in 2 days', days: 2 },
  { value: 'follow_up_7d', label: 'Follow up in 7 days', description: 'Remind me in a week', days: 7 },
  { value: 'schedule_meeting', label: 'Schedule meeting', description: 'Pick a date and time' },
  { value: 'move_stage', label: 'Move in Revenue Flow', description: 'Advance to the next Revenue Flow stage' },
  { value: 'none', label: 'No next step', description: 'Mark as complete' },
];

const pipelineStages = [
  'new',
  'contacted',
  'qualified',
  'proposal',
  'negotiation',
  'won',
  'lost',
];

export function NextStepModal({
  open,
  onOpenChange,
  leadName,
  outcome,
  onConfirm,
}: NextStepModalProps) {
  const [selectedStep, setSelectedStep] = useState<NextStepType>('follow_up_2d');
  const [meetingDate, setMeetingDate] = useState<string>('');
  const [meetingTime, setMeetingTime] = useState<string>('09:00');
  const [selectedStage, setSelectedStage] = useState<string>('qualified');

  const handleConfirm = () => {
    let nextStepDate: Date | undefined;
    
    if (selectedStep === 'follow_up_2d') {
      const date = new Date();
      date.setDate(date.getDate() + 2);
      date.setHours(9, 0, 0, 0);
      nextStepDate = date;
    } else if (selectedStep === 'follow_up_7d') {
      const date = new Date();
      date.setDate(date.getDate() + 7);
      date.setHours(9, 0, 0, 0);
      nextStepDate = date;
    } else if (selectedStep === 'schedule_meeting' && meetingDate) {
      const [hours, minutes] = meetingTime.split(':').map(Number);
      const date = new Date(meetingDate);
      date.setHours(hours, minutes, 0, 0);
      nextStepDate = date;
    }

    const stageForMove = selectedStep === 'move_stage' && selectedStage ? selectedStage : undefined;
    onConfirm({
      type: selectedStep,
      date: nextStepDate,
      stage: stageForMove,
    });
    
    // Reset state
    setSelectedStep('follow_up_2d');
    setMeetingDate('');
    setMeetingTime('09:00');
    setSelectedStage('qualified');
    onOpenChange(false);
  };

  const today = format(new Date(), 'yyyy-MM-dd');
  const minDate = today;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base font-medium">Next step</DialogTitle>
          <p className="text-sm text-muted-foreground">{leadName}</p>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <RadioGroup
            value={selectedStep}
            onValueChange={(v) => setSelectedStep(v as NextStepType)}
            className="space-y-2"
          >
            {nextStepPresets.map((preset) => {
              const isSelected = selectedStep === preset.value;
              
              return (
                <Label
                  key={preset.value}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                    isSelected
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/30"
                  )}
                >
                  <RadioGroupItem value={preset.value} />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{preset.label}</p>
                    <p className="text-xs text-muted-foreground">{preset.description}</p>
                  </div>
                </Label>
              );
            })}
          </RadioGroup>

          {/* Meeting date/time picker */}
          {selectedStep === 'schedule_meeting' && (
            <div className="space-y-3 pt-2 border-t border-border">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Date</Label>
                <Input
                  type="date"
                  value={meetingDate}
                  onChange={(e) => setMeetingDate(e.target.value)}
                  min={minDate}
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Time</Label>
                <Input
                  type="time"
                  value={meetingTime}
                  onChange={(e) => setMeetingTime(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
          )}

          {/* Stage selector */}
          {selectedStep === 'move_stage' && (
            <div className="space-y-2 pt-2 border-t border-border">
              <Label className="text-xs text-muted-foreground">Revenue Flow stage</Label>
              <select
                value={selectedStage}
                onChange={(e) => setSelectedStage(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {pipelineStages.map((stage) => (
                  <option key={stage} value={stage}>
                    {stage.charAt(0).toUpperCase() + stage.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={selectedStep === 'schedule_meeting' && !meetingDate}
          >
            Confirm
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

